import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, User, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { formatApiError, Spinner } from '../utils/helpers';
import { BUSINESS_TYPES } from '../config/businessTypes';
import api from '../services/api';

const STEPS = [
  { id: 1, title: 'Tipe Bisnis' },
  { id: 2, title: 'Info Toko' },
  { id: 3, title: 'Info Pemilik' },
];

function BusinessTypeCard({ typeKey, config, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(typeKey)}
      className={`relative w-full text-left p-4 rounded-2xl border-2 transition-all duration-150 hover:border-[#0f6c44] hover:shadow-md ${
        selected
          ? 'border-[#0f6c44] bg-emerald-50 shadow-sm'
          : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      {selected && (
        <CheckCircle size={18} className="absolute top-3 right-3 text-[#0f6c44]" />
      )}
      <span className="text-2xl mb-2 block">{config.emoji}</span>
      <p className={`font-bold text-sm font-manrope ${selected ? 'text-[#0f6c44]' : 'text-slate-800'}`}>
        {config.label}
      </p>
      <p className="text-xs text-slate-500 mt-1 leading-snug line-clamp-2">{config.description}</p>
    </button>
  );
}

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const isGoogleMode = new URLSearchParams(window.location.search).get('google') === 'true';
  const googleUser = isGoogleMode ? JSON.parse(sessionStorage.getItem('google_user') || '{}') : {};
  const [form, setForm] = useState({
    business_type: '', business_type_custom: '',
    store_name: '', store_phone: '', store_address: '',
    name: googleUser.name || '', email: googleUser.email || '', password: ''
  });
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const canProceedStep1 = !!form.business_type;
  const canProceedStep2 = !!form.store_name.trim();

  const handleNext = (e) => {
    e.preventDefault();
    if (step === 1 && !canProceedStep1) { toast.error('Pilih tipe bisnis terlebih dahulu'); return; }
    if (step === 2 && !canProceedStep2) { toast.error('Nama toko wajib diisi'); return; }
    if (step < 3) setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) { handleNext(e); return; }
    if (isGoogleMode) {
      if (!form.name || !googleUser.email) { toast.error('Data tidak lengkap'); return; }
      setLoading(true);
      try {
        await api.post('/auth/google-register', {
          email: googleUser.email, name: form.name, picture: googleUser.picture || '',
          store_name: form.store_name, store_phone: form.store_phone,
          store_address: form.store_address, business_type: form.business_type,
          business_type_custom: form.business_type_custom,
        });
        sessionStorage.removeItem('google_user');
        await refreshUser();
        toast.success(lang === 'id' ? 'Toko berhasil didaftarkan!' : 'Store registered!');
        navigate('/admin');
      } catch (err) {
        toast.error(formatApiError(err));
      } finally { setLoading(false); }
      return;
    }
    if (!form.name || !form.email || !form.password) { toast.error('Lengkapi semua field'); return; }
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      await refreshUser();
      toast.success(lang === 'id' ? 'Toko berhasil didaftarkan!' : 'Store registered!');
      navigate('/admin');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const selectedBT = BUSINESS_TYPES[form.business_type];

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="text-center mb-7">
          <div className="w-11 h-11 bg-[#0f6c44] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold text-xl font-manrope">K</span>
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 font-manrope">
            {lang === 'id' ? 'Daftar Toko Baru' : 'Register New Store'}
          </h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-7">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                step === s.id ? 'bg-[#0f6c44] text-white' :
                step > s.id ? 'bg-emerald-100 text-[#0f6c44]' :
                'bg-slate-100 text-slate-400'
              }`}>
                <span className="font-bold">{s.id}</span>
                <span>{s.title}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <form onSubmit={handleSubmit}>

            {/* ── Step 1: Tipe Bisnis ── */}
            {step === 1 && (
              <div>
                <p className="font-semibold text-slate-800 mb-1 font-manrope">Pilih Tipe Bisnis Anda *</p>
                <p className="text-xs text-slate-500 mb-5">Tipe bisnis mempengaruhi tampilan form pesanan, kanban, dan fitur yang tersedia.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(BUSINESS_TYPES).map(([key, cfg]) => (
                    <BusinessTypeCard
                      key={key} typeKey={key} config={cfg}
                      selected={form.business_type === key}
                      onSelect={v => setForm(f => ({ ...f, business_type: v }))}
                    />
                  ))}
                </div>
                {form.business_type && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#0f6c44] flex-shrink-0" />
                    <p className="text-sm text-[#0f6c44] font-medium">
                      {selectedBT?.emoji} <strong>{selectedBT?.label}</strong> dipilih
                    </p>
                  </div>
                )}
                {form.business_type === 'lainnya' && <div className="mt-4"><label className="mb-1.5 block text-sm font-medium text-slate-700">Jenis Usaha Anda <span className="text-slate-400">(opsional)</span></label><input value={form.business_type_custom} onChange={set('business_type_custom')} data-testid="custom-business-type-input" placeholder="Contoh: Salon, les privat, cuci kendaraan" className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#0f6c44]"/></div>}
              </div>
            )}

            {/* ── Step 2: Info Toko ── */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-slate-800 mb-1 font-manrope">Info Toko</p>
                  {selectedBT && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#0f6c44] font-medium bg-emerald-50 px-2.5 py-1 rounded-full mb-4">
                      {selectedBT.emoji} {selectedBT.label}
                    </span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Nama Toko *</label>
                  <input value={form.store_name} onChange={set('store_name')} required autoFocus
                    placeholder={`Nama ${selectedBT?.label || 'toko'} Anda`}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">No. Telepon / WhatsApp</label>
                  <input value={form.store_phone} onChange={set('store_phone')} type="tel"
                    placeholder="08xxxxxxxxxx"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Alamat Toko</label>
                  <textarea value={form.store_address} onChange={set('store_address')} rows={2}
                    placeholder="Jl. Merdeka No. 1, Jakarta"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800 resize-none" />
                </div>
              </div>
            )}

            {/* ── Step 3: Info Pemilik ── */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="font-semibold text-slate-800 mb-1 font-manrope">Info Pemilik / Akun</p>
                {isGoogleMode && (
                  <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                    {googleUser.picture && <img src={googleUser.picture} alt="avatar" className="w-8 h-8 rounded-full" />}
                    <div>
                      <p className="text-sm font-semibold text-green-800">{googleUser.email}</p>
                      <p className="text-xs text-green-600">Terhubung dengan Google</p>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Nama Lengkap Pemilik *</label>
                  <input value={form.name} onChange={set('name')} required autoFocus
                    placeholder="Nama lengkap"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800" />
                </div>
                {!isGoogleMode && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                      <input value={form.email} onChange={set('email')} required type="email"
                        placeholder="owner@email.com"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Password *</label>
                      <input value={form.password} onChange={set('password')} required type="password" minLength={6}
                        placeholder="Min. 6 karakter"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800" />
                    </div>
                  </>
                )}
                <div className="bg-slate-50 rounded-xl p-3.5 text-xs text-slate-500 space-y-1">
                  <p className="font-semibold text-slate-600">Ringkasan:</p>
                  <p>{selectedBT?.emoji} Tipe: <strong>{selectedBT?.label}</strong></p>
                  <p>Toko: <strong>{form.store_name}</strong></p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 mt-6">
              {step > 1 && (
                <button type="button" onClick={() => setStep(s => s - 1)}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors">
                  <ArrowLeft size={16} />Kembali
                </button>
              )}
              <button type="submit" disabled={loading || (step === 1 && !canProceedStep1)}
                data-testid="register-submit-button"
                className="flex-1 h-12 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60">
                {loading
                  ? <Spinner size="sm" className="border-white/30 border-t-white" />
                  : step < 3
                    ? <>{lang === 'id' ? 'Lanjut' : 'Next'} <ArrowRight size={18} /></>
                    : <>{lang === 'id' ? 'Daftar Sekarang' : 'Register Now'} <ArrowRight size={18} /></>
                }
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-5">
          Sudah punya akun?{' '}
          <Link to="/login" className="text-[#0f6c44] font-semibold hover:underline">Masuk</Link>
        </p>
      </div>
    </div>
  );
}
